import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { DialogState } from '../models/dialog-state';
import { DialogTypeEnum } from '../enums/dialog-type.enum';
import { CommonDialogComponent } from '../components/common-dialog/common-dialog.component';
import { DomSanitizer } from '@angular/platform-browser';
import { CaptchaService } from './captcha.service';
import { catchError, finalize, first, tap } from 'rxjs/operators';
import { CaptchaSettings } from '../models/сaptcha';
import { Confirmation, ConfirmationService } from 'primeng/api';
import { EtpFeeNotPaidComponent } from '../components/etp-fee-not-paid/etp-fee-not-paid.component';

@Injectable({
  providedIn: 'root',
})
export class DialogsService {
  private rootViewContainer: any;

  public dialogState = new BehaviorSubject<DialogState>(null);

  constructor(
    private factoryResolver: ComponentFactoryResolver,
    private _sanitizer: DomSanitizer,
    private _captchaService: CaptchaService,
    private _confirmationService: ConfirmationService
  ) {}

  setRootViewContainerRef(viewContainerRef) {
    this.rootViewContainer = viewContainerRef;
  }

  addDynamicComponent(state: DialogState) {
    const factory = this.factoryResolver.resolveComponentFactory(CommonDialogComponent);
    const component = factory.create(this.rootViewContainer?.parentInjector);
    component.instance.currentState = state;
    this.rootViewContainer.insert(component.hostView);
  }

  public show(state: DialogState) {
    this.addDynamicComponent(state);
    state.type = DialogTypeEnum.Info;
    this.dialogState.next(state);
  }

  public showSuccess(state: DialogState) {
    this.addDynamicComponent(state);
    state.type = DialogTypeEnum.Success;
    this.dialogState.next(state);
  }

  public showError(state: DialogState) {
    this.addDynamicComponent(state);
    state.type = DialogTypeEnum.Error;
    state.styleClass = 'error-dialog-window';
    this.dialogState.next(state);
  }

  public showErrorWithHtmlContent(state: DialogState) {
    const htmlContent = state.content as string;
    state.content = this._sanitizer.bypassSecurityTrustHtml(htmlContent) as string;
    this.addDynamicComponent(state);
    state.type = DialogTypeEnum.HtmlContent;
    this.dialogState.next(state);
  }

  public showWithHtmlContent(state: DialogState) {
    this.addDynamicComponent(state as DialogState);
    state.type = DialogTypeEnum.HtmlContent;
    state.styleClass = state.styleClass ? state.styleClass : 'error-dialog-window';
    this.dialogState.next(state as DialogState);
  }

  public showConfirm(state: Partial<DialogState>) {
    this.addDynamicComponent(state as DialogState);
    state.type = DialogTypeEnum.Confirm;
    state.styleClass = state.styleClass ? state.styleClass : 'error-dialog-window';
    this.dialogState.next(state as DialogState);
  }

  private _disableCaptcha(state: Partial<DialogState>): void {
    this._captchaService.isCaptchaVisible = false;
    this._captchaService.isCaptchaPassed = true;
    if (state.onCaptchaDisabled) {
      state.onCaptchaDisabled(null);
    }
  }

  public showCaptcha(state: Partial<DialogState>): void {
    this._captchaService.isCaptchaPassed = false;
    const sub = this._captchaService.getCaptchaSettings().pipe(
      first((value) => !!value),
      tap((settings: CaptchaSettings) => {
        if (settings) {
          if (settings?.IsEnabled === false) {
            this._disableCaptcha(state);
          } else {
            this._captchaService.settings = settings;
            this._captchaService.captchaHeader$ = settings.TokenHeaderName || '';
            this._captchaService.isCaptchaVisible = true;
            this.addDynamicComponent(state as DialogState);
            state.type = DialogTypeEnum.Captcha;
            state.styleClass = state.styleClass ? state.styleClass : 'captcha-dialog-window';
            this.dialogState.next(state as DialogState);
          }
        }
      }),
      catchError((err: Error) => {
        this._disableCaptcha(state);
        return of(err);
      }),
      finalize(() => {
        sub.unsubscribe();
      })
    ).subscribe();
  }

  public hide() {
    this.dialogState.next(null);
    this.rootViewContainer.clear();
  }

  public showHtmlContentForEtpFeeNotPaid(state: DialogState) {
    const factory = this.factoryResolver.resolveComponentFactory(EtpFeeNotPaidComponent);
    const component = factory.create(this.rootViewContainer.parentInjector);
    component.instance.currentState = state;
    this.rootViewContainer.insert(component.hostView);
  }

  public showInfo(confirmation: Confirmation): void {
    this._confirmationService.confirm({
      header: 'Внимание!',
      acceptLabel: 'Да',
      rejectVisible: false,
      icon: '',
      ...confirmation,
    });
  }

  public showInternetProblem() {
    this.showInfo({
      header: 'Внимание',
      message: 'Не удалось подключиться к серверу. Проверьте интернет-подключение и обновите страницу.',
      acceptLabel: 'Закрыть',
    });
  }
}
